<script lang="ts" setup>
import { computedAsync } from '@vueuse/core';
import adminAPI from '@/services/api';
import FlightOverview from '@/components/overview/FlightOverview.vue';
import { ClaimType, Flight, ReservationItinerary } from '@upclaim/sdk/src/data-contracts';
import { intervalToDuration, formatDuration } from 'date-fns';
import { fr } from 'date-fns/locale';
import { PlusIcon } from '@heroicons/vue/20/solid'
import BaseLoader from '../ui/loaders/BaseLoader.vue';
import { ref, watch } from 'vue';


const props = defineProps<{
  reservationId: string;
  impactedSegmentId: number;
  claimType: ClaimType;
}>();

const segments = ref<ReservationItinerary["segments"]>();

watch(() => props.reservationId, async () => {
  const reservation = await adminAPI.retrieveReservationItinerary(props.reservationId)
  segments.value = reservation.segments?.filter(s => s.cancelledBy !== 'CUSTOMER')
}, { immediate: true });

function formatConnectionTime(flightA: Flight, flightB: Flight) {
  if (!flightB) return;
  
  // Calculate the duration between the two flights
  const duration = intervalToDuration({
    start: new Date(flightA.scheduledArrivalUTC),
    end: new Date(flightB.scheduledDepartureUTC)
  });

  // Format the duration using formatDuration and French locale
  return formatDuration(duration, { locale: fr });
}
</script>
<template>
<div class="py-2">
  <div class="relative px-4">
    <div class="absolute inset-0 flex items-center" aria-hidden="true">
      <div class="w-full border-t border-gray-300" />
    </div>
    <div class="relative flex justify-start">
      <span class="px-3 text-base font-semibold leading-6 text-gray-900 bg-white">Début de la réservation</span>
    </div>
  </div>
  <!-- <pre>ici:{{ segments }}</pre> -->

  <div v-if="!segments?.length" class="py-4">
    <BaseLoader />
  </div>
  <div v-else>
    <div  v-for="(segment, index) of segments" class="text-gray-800">
      <!-- <pre>_______ waitlist: {{ segment.waitlistIndicator }} _______</pre> -->
      <!-- {{ segment.initialRecordNumber }} -->
      <!-- {{ segment.confirmationDate }} -->
      <div :class="[
        props.impactedSegmentId === segment.id ? 'border-2 my-5 relative border-indigo-500 rounded-lg mx-1 bg-indigo-50' : '',
        segment.reroutingProposal && segment.confirmationDate && !segment.cancelledBy ? 'bg-green-50 rounded-lg border-2 border-dotted border-green-500' : '',
        segment.reroutingProposal && !segment.confirmationDate ? 'bg-gray-100 rounded-lg border-2 border-dashed border-gray-500' : '',
      ]">

        <div v-if="impactedSegmentId === segment.id" class="flex mt-4 ml-2">
          <!-- <span class="px-3 text-sm font-normal leading-6 text-gray-900 bg-white border rounded-md">Segment détecté comme éligible</span> -->
          <span class="px-3 italic text-indigo-700">Segment detecté</span>
        </div>
        <div class="mt-4 ml-2" v-if="segment.reroutingProposal">
          <span v-if="segment.confirmationDate && !segment.cancelledBy" class="px-3 italic font-semibold leading-8 text-green-800">Proposition de réacheminement confirmée</span>
          <span v-else class="px-3 italic leading-8 text-gray-800">Proposition de réacheminement non confirmée</span>
          <!-- <pre>{{ segment.reroutingProposal }}</pre> -->
        </div>
        <FlightOverview
          class="bg-white"
          v-if="segment.cancelledBy !== 'CUSTOMER'"
          :airline="(segment.flight.airline as any)"
          :claim-type-name="props.impactedSegmentId === segment.id ? props.claimType : null"
          :flight="(segment.flight as any)"
          :rerouting-proposals="segment.reroutingProposals"
          :actual-rerouting="segment.actualRerouting"
          :cancelled-by="segment.cancelledBy"
          :cancelled-indicator="segment.cancellationType"
          :cancelled-signature="segment.cancellationSignature"
          :cancelled-date="segment.cancellationDate"
        />
      </div>


      <div class="relative py-2" v-if="formatConnectionTime(segment.flight, (segments as any)[index + 1]?.flight)">
        <div class="absolute inset-0 flex items-center" aria-hidden="true">
          <div class="w-full border-t border-gray-300 border-dashed" />
        </div>
        <div class="relative flex justify-center">
          <span class="inline-flex items-center gap-x-1.5 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 ">
            <PlusIcon class="-ml-1 -mr-0.5 h-5 w-5 text-gray-400" aria-hidden="true" />
            {{ formatConnectionTime(segment.flight, (segments as any)[index + 1]?.flight) }}
          </span>
        </div>
      </div>

      <!-- <div class="relative" v-if="formatConnectionTime(segment.flight, (segments as any)[index + 1]?.flight)">
        <div class="absolute inset-0 flex items-center" aria-hidden="true">
          <div class="w-full border-t border-gray-300" />
        </div>
        <div class="relative flex justify-center">
          <span class="px-2 text-sm text-gray-500 bg-white">
            + {{ formatConnectionTime(segment.flight, (segments as any)[index + 1]?.flight) }}
          </span>
        </div>
      </div> -->
    </div>
  </div>
  <div class="relative px-4 mt-8">
    <div class="absolute inset-0 flex items-center" aria-hidden="true">
      <div class="w-full border-t border-gray-300" />
    </div>
    <div class="relative flex justify-start">
      <span class="px-3 text-base font-semibold leading-6 text-gray-900 bg-white">Fin de la réservation</span>
    </div>
  </div>
</div>
</template>